'use client'

import { isEmpty } from 'lodash-es'
import { RefObject, useImperativeHandle } from 'react'
import { useForm } from 'react-hook-form'

import { ReviewStage } from '@/entities/contract'
import { OptionBox, Button, Dialog, Form, Illust } from '@/shared/ui'

import { useSimpleReviewTemplates } from '../../api/get-simple-review-templates'
import { useGuestSimpleReview } from '../../api/guest-simple-review'
import { REVIEW_ILLUST_MAP } from '../../lib/option'
import {
  convertToSelectedTemplateIds,
  getDefaultValues,
  ReviewFormValues,
} from '../../model/form'

export type ReviewFormRef = {
  submitForm: () => Promise<void>
}

type Props = {
  ref: RefObject<ReviewFormRef | null>
  initialContractId: number
  type: ReviewStage
  onClose: () => void
}

export const ReviewForm = ({
  ref,
  initialContractId,
  type,
  onClose,
}: Props) => {
  const { simpleReviewTemplates, templateIds } = useSimpleReviewTemplates()

  const { submitReview } = useGuestSimpleReview({ onSuccess: onClose })

  useImperativeHandle(ref, () => ({
    submitForm: async () => {
      await form.handleSubmit(submitForm)()
    },
  }))

  const form = useForm<ReviewFormValues>({
    defaultValues: getDefaultValues(templateIds),
  })

  const submitForm = async (formValues: ReviewFormValues) => {
    const selectedTemplateIds = convertToSelectedTemplateIds(formValues)

    if (isEmpty(selectedTemplateIds)) {
      onClose()

      return
    }

    await submitReview({
      initialContractId,
      body: {
        templateIds: selectedTemplateIds,
        reviewStage: type,
      },
    })
  }

  return (
    <>
      <Form.Root form={form} className="flex flex-col gap-2 px-6">
        {simpleReviewTemplates.map(template => (
          <OptionBox
            key={template.id}
            name={template.id.toString()}
            size="medium"
          >
            {isChecked => (
              <>
                <Illust
                  name={REVIEW_ILLUST_MAP[template.code]}
                  size={40}
                  active={isChecked}
                />
                <span>{template.content}</span>
              </>
            )}
          </OptionBox>
        ))}
      </Form.Root>
      <Dialog.Footer className="flex p-6">
        <Button
          type="button"
          variant="solid"
          color="primary"
          size="large"
          fullWidth
          onClick={form.handleSubmit(submitForm)}
        >
          완료
        </Button>
      </Dialog.Footer>
    </>
  )
}
